import { useBuilderState } from '../../hooks/useBuilderState';
import { useBuilderNode } from '../../hooks/useBuilderNode';
import {
  Eye, EyeOff, ChevronUp, ChevronDown, Trash2, ImageIcon, Type, Square, Circle, Triangle,
} from "lucide-react";
import type { DesignNode, ImageNodeData, ShapeNodeData, TextNodeData } from '../../types/node.types';

import { useState, useRef, useEffect, useCallback } from "react";
import { clsx } from "clsx";

const NodeTypeIcon = ({ type, shapeType }: { type: string; shapeType?: string }) => {
  const cls = "flex-shrink-0 text-[#aaa]";
  if (type === "image") return <ImageIcon size={11} strokeWidth={1.5} className={cls} />;
  if (type === "svg") return <ImageIcon size={11} strokeWidth={1.5} className={cls} />;
  if (type === "text") return <Type size={11} strokeWidth={1.5} className={cls} />;
  if (type === "shape" && shapeType === "circle") return <Circle size={11} strokeWidth={1.5} className={cls} />;
  if (type === "shape" && shapeType === "triangle") return <Triangle size={11} strokeWidth={1.5} className={cls} />;
  return <Square size={11} strokeWidth={1.5} className={cls} />;
};

export const LayerPanel = () => {
  const { surfaces, currentSurface, selectedNodeId, updateNode } = useBuilderState();
  const {
    selectNode, toggleNodeVisibility, bringToFront, sendToBack, deleteNode,
  } = useBuilderNode();
  const [draggedNodeId, setDraggedNodeId] = useState<string | null>(null);
  const [dragOverNodeId, setDragOverNodeId] = useState<string | null>(null);
  const [renamingNodeId, setRenamingNodeId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const renameInputRef = useRef<HTMLInputElement>(null);

  const currentLayers = surfaces[currentSurface] || [];
  const sortedLayers = [...currentLayers].sort((a, b) => b.zIndex - a.zIndex);

  const handleVisibilityToggle = (nodeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    toggleNodeVisibility(nodeId);
  };

  const handleBringToFront = (nodeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    bringToFront(nodeId);
  };

  const handleSendToBack = (nodeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    sendToBack(nodeId);
  };

  const handleDelete = (nodeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    deleteNode(nodeId);
  };

  const handleDragStart = (e: React.DragEvent, nodeId: string) => {
    setDraggedNodeId(nodeId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", nodeId);
    selectNode(nodeId);
  };

  const handleDragOver = (e: React.DragEvent, nodeId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverNodeId(nodeId);
  };

  const handleDrop = (e: React.DragEvent, targetNodeId: string) => {
    e.preventDefault();
    setDragOverNodeId(null);
    const draggedId = e.dataTransfer.getData("text/plain");
    if (draggedId && draggedId !== targetNodeId) {
      const draggedIndex = sortedLayers.findIndex((n) => n.id === draggedId);
      const targetIndex = sortedLayers.findIndex((n) => n.id === targetNodeId);
      if (draggedIndex !== -1 && targetIndex !== -1) {
        const newSortedLayers = [...sortedLayers];
        const [draggedItem] = newSortedLayers.splice(draggedIndex, 1);
        newSortedLayers.splice(targetIndex, 0, draggedItem);
        newSortedLayers.forEach((node, index) => {
          updateNode(node.id, { zIndex: newSortedLayers.length - index });
        });
      }
    }
    setDraggedNodeId(null);
  };

  const handleDragEnd = () => {
    setDraggedNodeId(null);
    setDragOverNodeId(null);
  };

  const startRename = useCallback((node: DesignNode) => {
    setRenamingNodeId(node.id);
    setRenameValue(node.name || getNodeDisplayName(node));
  }, []);

  const commitRename = useCallback(() => {
    if (renamingNodeId && renameValue.trim()) {
      updateNode(renamingNodeId, { name: renameValue.trim() });
    }
    setRenamingNodeId(null);
  }, [renamingNodeId, renameValue, updateNode]);

  const cancelRename = useCallback(() => {
    setRenamingNodeId(null);
  }, []);

  useEffect(() => {
    if (renamingNodeId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingNodeId]);

  const getNodeDisplayName = (node: DesignNode) => {
    if (node.name) return node.name;
    if (node.type === "image") {
      const d = node.data as ImageNodeData;
      const alt = d?.alt || "";
      if (alt) return alt;
      const filename = (d?.src || "").split("/").pop()?.split(".")[0] || "";
      if (filename) return filename.replace(/[-_]/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase());
      return `Image ${node.id.slice(-4)}`;
    }
    if (node.type === "shape") {
      const d = node.data as ShapeNodeData;
      const shapeType = d?.shapeType || "Shape";
      return shapeType.charAt(0).toUpperCase() + shapeType.slice(1);
    }
    if (node.type === "text") {
      const d = node.data as TextNodeData;
      const text = d?.text || "Text";
      return text.length > 22 ? text.slice(0, 22) + "…" : text;
    }
    return "Element";
  };

  return (
    <div className="flex flex-col h-full">


      {/* Section header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#e5e5e5]">
        <span className="text-[11px] font-semibold text-[#1a1a1a] tracking-tight">Layers</span>
        <span className="text-[10px] text-[#aaa]">{sortedLayers.length}</span>
      </div>

      {/* Layer rows */}
      <div className="flex-1 overflow-y-auto">
        {sortedLayers.length === 0 ? (
          <div className="flex items-center justify-center h-16">
            <p className="text-[11px] text-[#bbb]">No layers on this surface</p>
          </div>
        ) : (
          sortedLayers.map((node) => {
            const isDragged = draggedNodeId === node.id;
            const isDragOver = dragOverNodeId === node.id;
            const isSelected = selectedNodeId === node.id;
            const isHidden = !node.visible;

            return (
              <div
                key={node.id}
                className={clsx(
                  "group flex items-center gap-1.5 pl-3 pr-2 cursor-pointer select-none transition-colors",
                  "h-[26px]",
                  {
                    "bg-[#dce9ff]": isSelected,
                    "opacity-40": isDragged,
                    "bg-[#e8f0ff]": isDragOver && !isSelected,
                    "hover:bg-[#f0f0f0]": !isSelected && !isDragOver,
                  }
                )}
                draggable={renamingNodeId !== node.id}
                onDragStart={(e) => handleDragStart(e, node.id)}
                onDragOver={(e) => handleDragOver(e, node.id)}
                onDragLeave={() => setDragOverNodeId(null)}
                onDrop={(e) => handleDrop(e, node.id)}
                onDragEnd={handleDragEnd}
                onClick={(e) => { e.stopPropagation(); selectNode(node.id); }}
                onMouseDown={(e) => { if (e.button === 0) selectNode(node.id); }}
              >
                {/* Type icon */}
                <NodeTypeIcon type={node.type} shapeType={(node.data as ShapeNodeData)?.shapeType} />

                {/* Name */}
                {renamingNodeId === node.id ? (
                  <input
                    ref={renameInputRef}
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRename();
                      if (e.key === "Escape") cancelRename();
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 min-w-0 text-[11px] leading-none bg-white border border-blue-400 rounded px-1 py-px outline-none"
                  />
                ) : (
                  <span
                    className={clsx(
                      "flex-1 min-w-0 text-[11px] truncate leading-none",
                      isHidden ? "text-[#bbb]" : isSelected ? "text-[#1a1a1a]" : "text-[#333]"
                    )}
                    onDoubleClick={(e) => { e.stopPropagation(); startRename(node); }}
                  >
                    {getNodeDisplayName(node)}
                  </span>
                )}

                {/* Order + visibility + delete — hover only */}
                <div className="flex items-center gap-px opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => handleBringToFront(node.id, e)}
                    className="w-4 h-4 flex items-center justify-center rounded hover:bg-[#e0e0e0]"
                    title="Bring to front"
                  >
                    <ChevronUp size={10} strokeWidth={2} className="text-[#777]" />
                  </button>
                  <button
                    onClick={(e) => handleSendToBack(node.id, e)}
                    className="w-4 h-4 flex items-center justify-center rounded hover:bg-[#e0e0e0]"
                    title="Send to back"
                  >
                    <ChevronDown size={10} strokeWidth={2} className="text-[#777]" />
                  </button>
                  <button
                    onClick={(e) => handleVisibilityToggle(node.id, e)}
                    className="w-4 h-4 flex items-center justify-center rounded hover:bg-[#e0e0e0]"
                  >
                    {node.visible
                      ? <Eye size={11} strokeWidth={1.5} className="text-[#888]" />
                      : <EyeOff size={11} strokeWidth={1.5} className="text-[#bbb]" />
                    }
                  </button>
                  <button
                    onClick={(e) => handleDelete(node.id, e)}
                    className="w-4 h-4 flex items-center justify-center rounded hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 size={10} strokeWidth={1.5} className="text-[#cc4444]" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
